import { UIFormLabel } from '@/shared/ui/ui-form-label';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PaperClipIcon from '@/app/assets/icons/paperclip.svg?react';
import dayjs from 'dayjs';
import { DealRequestDocument } from '@/app/api/types';
import { DidoxPreviewModal } from '@/shared/modals/didox-preview-modal';
import { DidoxPreviewModalRef } from '@/shared/modals/didox-preview-modal/types';
import { useNotification } from '@/shared/contexts/NotificationContext';
import XmarkIcon from '@/app/assets/icons/xmark.svg?react';
import {
  useLazyGetFactoringContractTemplateQuery,
  useLazyGetRegisterOfDebtsTemplateQuery,
} from '@/app/api';
import { useParams } from 'react-router-dom';
import { UILoader } from '@/shared/ui/ui-loader';
import { getFetchEror } from '@/app/helpers';
import { useAppSelector } from '@/app/hooks';

interface Props {
  label: string;
  value: File | null | DealRequestDocument;
  documentType: 'contract' | 'registry' | 'general_agreement';
  onChange?: (value: File | null) => void;
  type: 'buyer' | 'seller' | 'factor';
  showReject?: boolean;
  showSign?: boolean;
  onReject?: () => void;
  onSign?: () => void;
  rejectLoading?: boolean;
  signLoading?: boolean;
  onDelete?: (document: DealRequestDocument) => void;
  onDeleteFile?: (document: File) => void;
  docType?: 'contract' | 'registry';
  isClosedFactoring?: boolean;
  isDisabled?: boolean;
  sendGovDocumentHandler?: () => void;
  onGenerateGeneralAgreement?: () => void;
  isDisabledGenerateGeneralAgreement?: boolean;
  isLoadingGenerateGeneralAgreement?: boolean;
}

export const FactorAddDocument: FC<Props> = ({
  label,
  value,
  onChange,
  type,
  showReject,
  showSign,
  onReject,
  onSign,
  rejectLoading,
  signLoading,
  documentType,
  onDelete,
  onDeleteFile,
  docType,
  isClosedFactoring,
  isDisabled = true,
  onGenerateGeneralAgreement,
  isDisabledGenerateGeneralAgreement,
  isLoadingGenerateGeneralAgreement,
}) => {
  const { t } = useTranslation();
  const { profile } = useAppSelector((state) => state.auth);
  const { showNotification } = useNotification();
  const [getFactoringContractTemplate] =
    useLazyGetFactoringContractTemplateQuery();
  const [getRegisterOfDebtsTemplate] = useLazyGetRegisterOfDebtsTemplateQuery();
  const inputRef = useRef<HTMLInputElement>(null);
  const didoxHtmlModalRef = useRef<DidoxPreviewModalRef>(null);
  const { id: dealId } = useParams<{ id: string }>();
  const { isFactoringContractTemplateAdded, isRegisterOfDebtsTemplateAdded } =
    profile || {};

  const [templateLoading, setTemplateLoading] = useState(false);

  const openDidoxHtmlModal = () => {
    if (!value || !('didoxId' in value)) return;

    didoxHtmlModalRef.current?.open({
      id: value.didoxId,
      name: value.name,
    });
  };

  const handePreviewFile = () => {
    if (!value || !(value instanceof File)) return;

    didoxHtmlModalRef.current?.open({
      file: value,
      name: value.name,
    });
  };

  const getStatus = () => {
    if (!(value && 'didoxId' in value)) return '';
    const status = t(`statuses.${type}.${value.status}`);

    return status;
  };

  const getTemplates = async () => {
    try {
      setTemplateLoading(true);
      const response =
        docType === 'contract'
          ? await getFactoringContractTemplate({
              id: Number(dealId),
            })
          : await getRegisterOfDebtsTemplate({
              id: Number(dealId),
            });

      if (response.data?.base64) {
        const base64Content = response.data.base64.includes(',')
          ? response.data.base64.split(',')[1]
          : response.data.base64;

        const byteArray = Uint8Array.from(atob(base64Content), (c) =>
          c.charCodeAt(0)
        );
        const file = new File(
          [byteArray],
          docType === 'contract'
            ? t('template_name.contract')
            : t('template_name.registry'),
          {
            type: 'application/pdf',
          }
        );
        onChange?.(file);
        return;
      }

      showNotification(getFetchEror(response.error) || t('error'), 'error');
    } catch (error) {
      showNotification(getFetchEror(error) || t('error'), 'error');
    } finally {
      setTemplateLoading(false);
    }
  };

  const isContractAddedForClosedFactoring =
    isFactoringContractTemplateAdded && isClosedFactoring;

  const isTemplatesAddedForOpenAndReverseFactoring =
    isFactoringContractTemplateAdded &&
    isRegisterOfDebtsTemplateAdded &&
    !isClosedFactoring;

  if (value && 'didoxId' in value) {
    return (
      <Box
        sx={{
          px: 2,
          py: 1,
          border: '1px solid rgba(230, 241, 255, 1)',
          borderRadius: '6px',
          position: 'relative',
          backgroundColor: '#FFF',
        }}
      >
        <Grid container>
          <Grid item sm={4} xs={12}>
            <UIFormLabel
              sx={{
                fontSize: 13,
                color: 'rgba(11, 19, 112, .5)',
                lineHeight: 1,
              }}
            >
              {value.name}
            </UIFormLabel>
            <Typography fontSize={13}>
              {t('from_value', {
                date: dayjs(value.createdAt, 'DD.MM.YYYY').format('DD.MM.YYYY'),
              })}
            </Typography>
          </Grid>
          <Grid item sm={4} xs={12}>
            <UIFormLabel
              sx={{
                fontSize: 13,
                color: 'rgba(11, 19, 112, .5)',
                lineHeight: 1,
              }}
            >
              {getStatus()}
            </UIFormLabel>

            <Typography fontSize={13}>
              {documentType === 'contract'
                ? value.signers.find((el) => el.actor === 'seller')?.comment
                : value.signers.find((el) => el.actor === 'buyer')?.comment ||
                  value.signers.find((el) => el.actor === 'seller')?.comment}
            </Typography>
          </Grid>
          <Grid item sm={4} xs={12} sx={{ alignSelf: 'center' }}>
            <Stack
              direction='row'
              spacing={1.5}
              justifyContent='flex-end'
              alignItems='center'
            >
              <Button color='inherit' onClick={openDidoxHtmlModal}>
                {t('look')}
              </Button>
              {showReject && (
                <Button
                  color='error'
                  onClick={() => onReject?.()}
                  disabled={rejectLoading}
                >
                  {t('reject')}
                </Button>
              )}
              {showSign && (
                <Button onClick={() => onSign?.()} disabled={signLoading}>
                  {t('sign')}
                </Button>
              )}
              {onDelete && (
                <IconButton onClick={() => onDelete(value)}>
                  <XmarkIcon />
                </IconButton>
              )}
            </Stack>
          </Grid>
        </Grid>

        <DidoxPreviewModal ref={didoxHtmlModalRef} showHtml={false} />
      </Box>
    );
  }

  if (documentType === 'general_agreement') {
    return (
      <Stack direction='row' spacing={1}>
        <Box
          sx={{
            flex: 1,
            borderWidth: 1,
            borderColor: 'rgba(230, 241, 255, 1)',
            borderStyle: 'solid',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            backgroundColor: '#FFF',
          }}
        >
          <Grid container>
            <Grid
              item
              sm={4}
              xs={12}
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {value ? (
                <Box>
                  <UIFormLabel
                    sx={{
                      fontSize: 13,
                      color: 'rgba(11, 19, 112, .5)',
                      lineHeight: 1,
                      mb: 0,
                    }}
                  >
                    {value?.name}
                  </UIFormLabel>
                  <Typography fontSize={13}>
                    {t('from_value', {
                      date: dayjs().format('DD.MM.YYYY'),
                    })}
                  </Typography>
                </Box>
              ) : (
                <UIFormLabel required sx={{ margin: 0 }}>
                  {label}
                </UIFormLabel>
              )}
            </Grid>
            <Grid item sm={4} xs={12}>
              <UIFormLabel
                sx={{
                  fontSize: 13,
                  color: 'rgba(11, 19, 112, .5)',
                  lineHeight: 1,
                }}
              >
                {getStatus()}
              </UIFormLabel>
            </Grid>
            <Grid item sm={4} xs={12} sx={{ alignSelf: 'center' }}>
              <Stack
                direction='row'
                spacing={1.5}
                justifyContent='flex-end'
                alignItems='center'
              >
                {value && (
                  <Button color='inherit' onClick={handePreviewFile}>
                    {t('look')}
                  </Button>
                )}
                {!value && (
                  <Stack direction='row' alignItems='center' spacing={3}>
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {isLoadingGenerateGeneralAgreement ? (
                        <UILoader size={20} />
                      ) : (
                        <Tooltip
                          title={t('cannot_generate_document_tooltip')}
                          placement='top'
                        >
                          <Typography
                            fontSize='1.4rem'
                            fontWeight={600}
                            color='primary.main'
                            onClick={() => {
                              if (!isDisabledGenerateGeneralAgreement) {
                                onGenerateGeneralAgreement?.();
                              }
                            }}
                            sx={{
                              cursor: isDisabledGenerateGeneralAgreement
                                ? 'not-allowed'
                                : 'pointer',
                              opacity: isDisabledGenerateGeneralAgreement
                                ? 0.5
                                : 1,
                            }}
                          >
                            {t('generate')}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>

                    <Button
                      color='inherit'
                      onClick={() => {
                        inputRef.current?.click();
                      }}
                      endIcon={
                        <Box
                          component={PaperClipIcon}
                          sx={{ height: 20, width: 20 }}
                        />
                      }
                    >
                      {t('upload_from_pc')}
                    </Button>
                  </Stack>
                )}
                {value && onDeleteFile && (
                  <Tooltip title={t('delete_file')}>
                    <IconButton
                      onClick={() => {
                        onDeleteFile?.(value);
                        // Очищаем input при удалении файла
                        if (inputRef.current) {
                          inputRef.current.value = '';
                        }
                      }}
                    >
                      <XmarkIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <input
          ref={inputRef}
          type='file'
          accept='.pdf'
          style={{ display: 'none' }}
          onChange={(e) => {
            if (
              e.target.files?.[0] &&
              e.target.files?.[0].type !== 'application/pdf'
            ) {
              return showNotification(t('errors:only_pdf_allowed'), 'error');
            }
            if (e.target.files && e.target.files.length > 0) {
              onChange?.(e.target.files[0]);
            }
            e.target.value = '';
          }}
        />
        <DidoxPreviewModal ref={didoxHtmlModalRef} showPdf showHtml={false} />
      </Stack>
    );
  }

  return (
    <Stack direction='row' spacing={1}>
      <Box
        sx={{
          flex: 1,
          borderWidth: 1,
          borderColor: 'rgba(230, 241, 255, 1)',
          borderStyle: 'solid',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          backgroundColor: '#FFF',
        }}
      >
        <Grid container>
          <Grid
            item
            sm={4}
            xs={12}
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {value ? (
              <Box>
                <UIFormLabel
                  sx={{
                    fontSize: 13,
                    color: 'rgba(11, 19, 112, .5)',
                    lineHeight: 1,
                    mb: 0,
                  }}
                >
                  {value?.name}
                </UIFormLabel>
                <Typography fontSize={13}>
                  {t('from_value', {
                    date: dayjs().format('DD.MM.YYYY'),
                  })}
                </Typography>
              </Box>
            ) : (
              <UIFormLabel required sx={{ margin: 0 }}>
                {label}
              </UIFormLabel>
            )}
          </Grid>
          <Grid item sm={4} xs={12}>
            <UIFormLabel
              sx={{
                fontSize: 13,
                color: 'rgba(11, 19, 112, .5)',
                lineHeight: 1,
              }}
            >
              {getStatus()}
            </UIFormLabel>
          </Grid>
          <Grid item sm={4} xs={12} sx={{ alignSelf: 'center' }}>
            <Stack
              direction='row'
              spacing={1.5}
              justifyContent='flex-end'
              alignItems='center'
            >
              {value && (
                <Button color='inherit' onClick={handePreviewFile}>
                  {t('look')}
                </Button>
              )}
              {!value && (
                <Stack direction='row' alignItems='center' spacing={3}>
                  {(isTemplatesAddedForOpenAndReverseFactoring ||
                    isContractAddedForClosedFactoring) && (
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {templateLoading ? (
                        <UILoader size={20} />
                      ) : (
                        <Tooltip
                          title={
                            isDisabled && t('cannot_generate_document_tooltip')
                          }
                          placement='top'
                        >
                          <Typography
                            fontSize='1.4rem'
                            fontWeight={600}
                            color='primary.main'
                            onClick={
                              templateLoading || isDisabled
                                ? undefined
                                : getTemplates
                            }
                            sx={{
                              cursor:
                                templateLoading || isDisabled
                                  ? 'not-allowed'
                                  : 'pointer',
                              opacity: isDisabled ? 0.5 : 1,
                            }}
                          >
                            {t('generate')}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  <Button
                    color='inherit'
                    onClick={() => {
                      inputRef.current?.click();
                    }}
                    endIcon={
                      <Box
                        component={PaperClipIcon}
                        sx={{ height: 20, width: 20 }}
                      />
                    }
                    disabled={isDisabled}
                  >
                    {t('upload')}
                  </Button>
                </Stack>
              )}
              {value && onDeleteFile && (
                <Tooltip title={t('delete_file')}>
                  <IconButton
                    onClick={() => {
                      onDeleteFile?.(value);
                      // Очищаем input при удалении файла
                      if (inputRef.current) {
                        inputRef.current.value = '';
                      }
                    }}
                  >
                    <XmarkIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <input
        ref={inputRef}
        type='file'
        accept='.pdf'
        style={{ display: 'none' }}
        onChange={(e) => {
          if (
            e.target.files?.[0] &&
            e.target.files?.[0].type !== 'application/pdf'
          ) {
            return showNotification(t('errors:only_pdf_allowed'), 'error');
          }
          if (e.target.files && e.target.files.length > 0) {
            onChange?.(e.target.files[0]);
          }
          e.target.value = '';
        }}
      />
      <DidoxPreviewModal ref={didoxHtmlModalRef} showPdf showHtml={false} />
    </Stack>
  );
};
